import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { SuggestBox } from './suggest-box';

/** Schools are stored as one text, each school apart from the next by this (roadmap §7.19). */
const SEPARATOR = ';';

/** The schools a stored answer names, one per box; at least one box, empty if none. */
function splitSchools(value: string): string[] {
  const parts = value
    .split(SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part !== '');
  return parts.length > 0 ? parts : [''];
}

/** The answer the boxes compose: the schools typed, in order, `; `-joined, empty boxes left out. */
function joinSchools(parts: string[]): string {
  return parts
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join(`${SEPARATOR} `);
}

/**
 * A school answer as one box per school (the survey review's T9, R13; the owner's Q9 d), each box a
 * `SuggestBox` of US colleges, with "Add another school" under them and a remove beside every box
 * but a lone one — a `text_short` usage or a `text` column whose `suggest` is `colleges`.
 *
 * **The answer is unchanged by the boxes**: one text, the schools `; `-joined, as survey-api's
 * roadmap §7.19 stores them; an empty box adds nothing. The rejected alternative — several schools
 * typed into one box between semicolons — is what the client called messy to fill in and to read.
 * A stored answer is drawn a box per school; a `;` typed into a box splits it when the box is left.
 *
 * The boxes are this field's own state while the respondent works in them, so an added empty box
 * stays; a value that moves under them (a re-read, a reload) draws them afresh.
 */
export function SchoolList({
  id,
  label,
  value,
  disabled,
  invalid,
  maxLength,
  className,
  onChange,
  onCommit
}: {
  /** The first box's id, which the question's `label` names; without one, `label` names it. */
  id?: string;
  /** The question's or the column's words, which name each further box and its remove. */
  label: string;
  value: string;
  disabled?: boolean;
  invalid?: boolean;
  maxLength?: number;
  className?: string;
  onChange: (value: string) => void;
  onCommit?: () => void;
}) {
  const [parts, setParts] = useState(() => splitSchools(value));
  const list = useRef<HTMLDivElement>(null);
  // The box to focus once drawn — the one "Add another school" just added.
  const [focusAt, setFocusAt] = useState<number | null>(null);

  useEffect(() => {
    // Only a value moved from outside redraws the boxes; the boxes' own edits compose it.
    if (joinSchools(parts) !== value) setParts(splitSchools(value));
  }, [value]);

  useEffect(() => {
    if (focusAt === null) return;
    list.current?.querySelectorAll<HTMLInputElement>('input[role="combobox"]')[focusAt]?.focus();
    setFocusAt(null);
  }, [focusAt]);

  const edit = (next: string[]) => {
    setParts(next);
    onChange(joinSchools(next));
  };

  return (
    <div ref={list} className="grid gap-2">
      {parts.map((part, index) => (
        <div key={index} className="flex items-center gap-2">
          <SuggestBox
            id={index === 0 ? id : undefined}
            aria-label={index === 0 && id ? undefined : `${label}, school ${index + 1}`}
            className={cn('aria-invalid:ring-[3px]', className)}
            value={part}
            disabled={disabled}
            maxLength={maxLength}
            aria-invalid={invalid}
            onChange={(text) => edit(parts.map((each, at) => (at === index ? text : each)))}
            onBlur={() => {
              // A `;` typed into one box is two schools: drawn as two once the box is left.
              if (part.includes(SEPARATOR)) setParts(splitSchools(joinSchools(parts)));
              onCommit?.();
            }}
          />
          {parts.length > 1 && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Remove ${part.trim() || `school ${index + 1}`} from: ${label}`}
              onClick={() => {
                edit(parts.filter((_, at) => at !== index));
                onCommit?.();
              }}
            >
              <X />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="justify-self-start"
          onClick={() => {
            setParts([...parts, '']);
            setFocusAt(parts.length);
          }}
        >
          <Plus />
          Add another school
        </Button>
      )}
    </div>
  );
}
